import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { typography, vault, vaultEffects, vaultLayout, vaultMedia, vaultRadii } from './design-tokens';

const focusRing = {
  '&:focus-visible': { outline: `2px solid ${vault.accent}`, outlineOffset: 2 },
};

// The two columns (info | signature) stack here, per the tablet spec.
const stackColumns = '@media (max-width: 900px)';

export const artistStyles: Record<string, SystemStyleObject<Theme>> = {
  page: {
    minHeight: '100vh',
    backgroundColor: vault.bg,
    color: vault.fg,
  },
  statusMessage: {
    padding: `64px ${vaultLayout.gutter}`,
    textAlign: 'center',
    fontSize: 16,
    color: vault.muted,
    [vaultMedia.mobile]: { padding: `40px ${vaultLayout.gutterMobile}` },
  },

  // ─── Banner ────────────────────────────────────────────────────────────────
  banner: {
    position: 'relative',
    height: 440,
    overflow: 'hidden',
    background: vaultEffects.stripes,
    '@media (max-width: 900px)': { height: 380 },
    [vaultMedia.mobile]: { height: 300 },
  },
  bannerImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  bannerFade: {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(180deg, rgba(0,0,0,0) 30%, ${vault.bg} 100%)`,
    pointerEvents: 'none',
  },
  bannerOverlay: {
    position: 'absolute',
    left: vaultLayout.gutter,
    right: vaultLayout.gutter,
    bottom: 36,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    [vaultMedia.mobile]: {
      left: vaultLayout.gutterMobile,
      right: vaultLayout.gutterMobile,
      bottom: 20,
      gap: '6px',
    },
  },
  bannerName: {
    margin: 0,
    fontSize: 84,
    lineHeight: 0.95,
    fontWeight: 600,
    letterSpacing: '-0.04em',
    color: vault.fg,
    textWrap: 'balance',
    overflowWrap: 'anywhere',
    '@media (max-width: 900px)': { fontSize: 64 },
    [vaultMedia.mobile]: { fontSize: 46 },
  },
  bannerAltName: {
    fontSize: 15,
    color: vault.muted,
    [vaultMedia.mobile]: { fontSize: 13 },
  },
  verifiedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: '5px',
    padding: '3px 10px',
    borderRadius: vaultRadii.pill,
    backgroundColor: vault.okBg,
    color: vault.ok,
    border: `1px solid ${vault.ok}`,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  // ─── Sticky rail ───────────────────────────────────────────────────────────
  rail: {
    position: 'sticky',
    top: 0,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: `0 ${vaultLayout.gutter}`,
    padding: '14px 0',
    backgroundColor: vault.bg,
    borderTop: `1px solid ${vault.line}`,
    borderBottom: `1px solid ${vault.line}`,
    [vaultMedia.mobile]: {
      position: 'static',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '10px',
      margin: `0 ${vaultLayout.gutterMobile}`,
      padding: '4px 0 0',
      border: 'none',
    },
  },
  railName: {
    fontSize: 15,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    [vaultMedia.mobile]: { display: 'none' },
  },
  railSpacer: {
    flex: 1,
    [vaultMedia.mobile]: { display: 'none' },
  },
  signingPill: {
    fontSize: 13,
    padding: '8px 14px',
    [vaultMedia.mobile]: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: '12px',
      fontSize: 14,
      '& > .glow-pill-text': { flex: 1, display: 'flex', justifyContent: 'space-between', gap: '12px' },
    },
  },
  signingPillLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  railButton: {
    minWidth: 0,
    padding: '8px 18px',
    borderRadius: '9px',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    gap: '6px',
    '& .MuiButton-startIcon': { margin: 0 },
    '&:hover': { boxShadow: 'none' },
    ...focusRing,
    [vaultMedia.mobile]: { padding: '13px', borderRadius: '12px', fontSize: 15 },
  },
  followButton: {
    backgroundColor: vault.fg,
    color: vault.bg,
    border: `1px solid ${vault.fg}`,
    '&:hover': { backgroundColor: vault.fg, opacity: 0.88 },
  },
  followingButton: {
    backgroundColor: vault.glow,
    color: vault.accentText,
    border: `1px solid ${vault.accent}`,
    '&:hover': { backgroundColor: vault.glowStrong },
  },
  editButton: {
    backgroundColor: 'transparent',
    color: vault.fg,
    border: `1px solid ${vault.lineStrong}`,
    '&:hover': { backgroundColor: vault.chip },
    [vaultMedia.mobile]: { display: 'none' },
  },
  // Same look as editButton but stays visible on phones, where artists are likely to be.
  ownerEditButton: {
    backgroundColor: 'transparent',
    color: vault.fg,
    border: `1px solid ${vault.lineStrong}`,
    '&:hover': { backgroundColor: vault.chip },
  },

  // ─── Link cards ────────────────────────────────────────────────────────────
  linkCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    padding: `28px ${vaultLayout.gutter} 0`,
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    [vaultMedia.mobile]: { padding: `20px ${vaultLayout.gutterMobile} 0` },
  },
  linkCard: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '28px',
    minWidth: 0,
    padding: '18px',
    borderRadius: vaultRadii.card,
    textDecoration: 'none',
    transition: `border-color ${vaultEffects.transition}, background-color ${vaultEffects.transition}, opacity ${vaultEffects.transition}`,
    ...focusRing,
    [vaultMedia.mobile]: { gap: '20px', padding: '16px 14px' },
  },
  linkCardPrimary: {
    backgroundColor: vault.accent,
    color: vault.onAccent,
    border: `1px solid ${vault.accent}`,
    '&:hover': { opacity: 0.9 },
  },
  linkCardSecondary: {
    backgroundColor: vault.surface,
    color: vault.fg,
    border: `1px solid ${vault.line}`,
    '&:hover': { borderColor: vault.accent },
  },
  linkCardEyebrow: {
    fontFamily: typography.fontFamily.mono,
    fontSize: 11,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  linkCardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
    '& svg': { flex: 'none' },
  },
  // Full text on desktop, shortened text on mobile.
  desktopOnly: {
    [vaultMedia.mobile]: { display: 'none' },
  },
  mobileOnly: {
    display: 'none',
    [vaultMedia.mobile]: { display: 'inline' },
  },

  // ─── Body columns ──────────────────────────────────────────────────────────
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 380px',
    gap: '48px',
    alignItems: 'start',
    padding: `40px ${vaultLayout.gutter} 64px`,
    [stackColumns]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: '40px' },
    [vaultMedia.mobile]: { padding: `28px ${vaultLayout.gutterMobile} 40px`, gap: '32px' },
  },
  mainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    minWidth: 0,
    [vaultMedia.mobile]: { gap: '32px' },
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '4px 16px',
    marginBottom: '8px',
  },
  collectionSummary: {
    fontSize: 13,
    color: vault.muted,
  },

  // ─── From the artist ───────────────────────────────────────────────────────
  artistNote: {
    padding: '20px 22px',
    borderRadius: vaultRadii.panel,
    backgroundColor: vault.surface,
    border: `1px solid ${vault.line}`,
    borderLeft: `3px solid ${vault.accent}`,
    [vaultMedia.mobile]: { padding: '16px 18px' },
  },
  artistNoteText: {
    margin: '10px 0 0',
    fontSize: 15,
    lineHeight: 1.6,
    color: vault.fg,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
  artistNoteMeta: {
    fontSize: 13,
    color: vault.muted,
  },

  // ─── Info list ─────────────────────────────────────────────────────────────
  infoList: {
    margin: 0,
  },
  infoRow: {
    display: 'grid',
    gridTemplateColumns: '200px minmax(0, 1fr)',
    gap: '24px',
    padding: '16px 0',
    borderBottom: `1px solid ${vault.line}`,
    fontSize: 15,
    lineHeight: 1.5,
    [vaultMedia.mobile]: { gridTemplateColumns: 'minmax(0, 1fr)', gap: '6px', padding: '14px 0' },
  },
  infoLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: vault.muted,
    [vaultMedia.mobile]: { fontSize: 12 },
  },
  infoValue: {
    margin: 0,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    color: vault.fg,
    overflowWrap: 'anywhere',
    [vaultMedia.mobile]: { fontSize: 16 },
  },
  infoLink: {
    color: vault.fg,
    textDecoration: 'none',
    borderBottom: `1px solid ${vault.lineStrong}`,
    transition: `border-color ${vaultEffects.transition}, color ${vaultEffects.transition}`,
    '&:hover': { color: vault.accentText, borderColor: vault.accent },
    ...focusRing,
  },
  helpIcon: {
    display: 'inline-flex',
    color: vault.faint,
    cursor: 'help',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 11px',
    borderRadius: vaultRadii.input,
    backgroundColor: vault.chip,
    border: `1px solid ${vault.line}`,
    color: vault.fg,
    fontSize: 13,
    lineHeight: 1.4,
    textDecoration: 'none',
    transition: `border-color ${vaultEffects.transition}, background-color ${vaultEffects.transition}`,
    '& svg': { flex: 'none', color: vault.muted },
    '&:hover': { borderColor: vault.accent, backgroundColor: vault.chipStrong },
    ...focusRing,
    [vaultMedia.mobile]: { minHeight: 40, padding: '8px 12px', fontSize: 14 },
  },
  okChip: {
    display: 'inline-flex',
    padding: '4px 10px',
    borderRadius: vaultRadii.pill,
    backgroundColor: vault.okBg,
    color: vault.ok,
    border: `1px solid ${vault.ok}`,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  neutralChip: {
    display: 'inline-flex',
    padding: '4px 10px',
    borderRadius: vaultRadii.pill,
    backgroundColor: vault.chip,
    color: vault.muted,
    border: `1px solid ${vault.lineStrong}`,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.3,
  },

  // ─── Events ────────────────────────────────────────────────────────────────
  eventsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    marginTop: '14px',
    [vaultMedia.mobile]: { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  eventCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    minWidth: 0,
    padding: '16px',
    borderRadius: vaultRadii.card,
    backgroundColor: vault.surface,
    border: `1px solid ${vault.line}`,
    color: vault.fg,
    cursor: 'pointer',
    transition: `border-color ${vaultEffects.transition}`,
    '&:hover': { borderColor: vault.accent },
    ...focusRing,
  },
  dateBlock: {
    width: 56,
    flex: 'none',
    textAlign: 'center',
    padding: '8px 0',
    borderRadius: vaultRadii.input,
    backgroundColor: vault.chip,
    border: `1px solid ${vault.line}`,
  },
  dateBlockMonth: {
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    color: vault.accentText,
  },
  dateBlockDay: {
    fontSize: 22,
    fontWeight: 600,
    lineHeight: 1.2,
  },
  eventText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: 0,
  },
  eventName: {
    fontSize: 15,
    fontWeight: 600,
    color: vault.fg,
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': { color: vault.accentText },
    ...focusRing,
  },
  eventMeta: {
    fontSize: 13,
    color: vault.muted,
  },
  lastAttended: {
    marginTop: '14px',
    fontSize: 13,
    color: vault.muted,
    '& a': {
      color: vault.fg,
      textDecoration: 'none',
      '&:hover': { color: vault.accentText },
      ...focusRing,
    },
  },

  // ─── Side column ───────────────────────────────────────────────────────────
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    minWidth: 0,
    [stackColumns]: { gap: '32px' },
  },
  signature: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  signatureSlab: {
    width: '100%',
    [stackColumns]: { maxWidth: 380 },
    [vaultMedia.mobile]: { maxWidth: 220 },
  },
  signatureCaption: {
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    color: vault.muted,
    margin: 0,
  },
  signatureThumbs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  signatureThumb: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    width: 72,
    padding: 0,
    border: 'none',
    background: 'none',
    color: vault.muted,
    cursor: 'pointer',
    fontFamily: typography.fontFamily.mono,
    fontSize: 10,
    lineHeight: 1.2,
    textAlign: 'center',
    borderRadius: '8px',
    '& img': {
      width: '100%',
      aspectRatio: '63 / 88',
      objectFit: 'cover',
      borderRadius: '6px',
      border: `2px solid ${vault.line}`,
      background: vaultEffects.stripes,
      transition: `border-color ${vaultEffects.transition}`,
    },
    '&:hover img': { borderColor: vault.muted },
    '&[aria-pressed="true"]': { color: vault.fg },
    '&[aria-pressed="true"] img': { borderColor: vault.accent },
    ...focusRing,
  },
  signatureThumbLabel: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // ─── News (ArtistNewsSection) ──────────────────────────────────────────────
  newsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '14px',
  },
  newsCard: {
    display: 'block',
    width: '100%',
    padding: '16px',
    borderRadius: vaultRadii.card,
    backgroundColor: vault.surface,
    border: `1px solid ${vault.line}`,
    color: vault.fg,
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    transition: `border-color ${vaultEffects.transition}`,
    '&:hover': { borderColor: vault.accent },
    ...focusRing,
  },
  newsTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.35,
    '& svg': { flex: 'none', marginTop: '2px', color: vault.accentText },
  },
  newsDate: {
    marginTop: '6px',
  },
  newsSummary: {
    marginTop: '8px',
    fontSize: 13,
    lineHeight: 1.5,
    color: vault.muted,
  },
  newsAllLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    alignSelf: 'flex-start',
    padding: '6px 0',
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    color: vault.accentText,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
    ...focusRing,
  },
};
